import { SequenceHelper } from "@swivl-mono/helpers";
import { AllAPIScopePermissions, LocationVarKey, SequenceEnabledStatus, SequenceInterface, SequenceSubType } from "@swivl-mono/types";
import { OptionsHelper } from "@swivl-mono/ui";
import { useEffect, useMemo } from "react";

import { IS_EQUAL, IS_NOT_EQUAL, IF_ANY } from "../../../../Components/Worksheets/Types/Worksheet.Interface";
import {
  DropdownRow,
  InputRow,
  PageHeader,
  Section,
  SectionHeader,
  SwitchRow,
  TextAreaRow
} from "../../../../Components/Worksheets/WorksheetComponent/WorksheetComponents";
import { usePropertiesGetLocationIds } from "../../../../Hooks/usePropertiesGetLocationIds";
import SequencesModel from "../../../../Models/Sequences/SequencesModel";

export default function WorksheetView() {
  const { sequences } = SequencesModel.useState();
  const page = usePropertiesGetLocationIds();
  useEffect(() => {
    SequencesModel.load(false);
  }, []);
  const sequenceArray: SequenceInterface[] | undefined = useMemo(() => {
    if (sequences) {
      return Object.values(sequences);
    }
    return undefined;
  }, [sequences]);


  return (
    <>
      <PageHeader
        {...page}
        subheading="These sync with your FMS daily and will trigger a workflow to communicate with your customers around past due payments."
      >
        Past Due Payment Sequences
      </PageHeader>
      <SwitchRow {...page} keyName={LocationVarKey.WantsCronReceiptsPastDuePayments} label="Send Email Receipts" />
      <SwitchRow {...page} keyName={LocationVarKey.FMSplusPastDueShortlinksEnabled} label="Use Shortlinks" />

      {sequenceArray && SequenceHelper.isAnySequenceSubType(SequenceSubType.PAST_DUE_DAY_00, sequenceArray) && (
        <>
          <SectionHeader {...page}>Day that payment is due</SectionHeader>
          <DropdownRow
            {...page}
            label="Enabled"
            marginTop
            keyName={LocationVarKey.SequencePastDueDay00Enabled}
            options={[
              { label: "Enabled", value: SequenceEnabledStatus.ENABLED },
              { label: "Disabled", value: SequenceEnabledStatus.DISABLED },
              { label: "Simulated", value: SequenceEnabledStatus.SIMULATED },
            ]}
          />
          <Section {...page} hideIf={IS_EQUAL(LocationVarKey.SequencePastDueDay00Enabled, SequenceEnabledStatus.DISABLED)}>
            <DropdownRow
              {...page}
              label="Time"
              marginTop
              keyName={LocationVarKey.SequencePastDueDay00TimeInUTC}
              options={OptionsHelper.hoursOptionsLocalized()}
            />
            <h6>
              <b>Channel</b>
            </h6>
            <br></br>
            <SwitchRow {...page} hideIf={IS_NOT_EQUAL(LocationVarKey.swivlHasVoice, "1")} keyName={LocationVarKey.FMSplus0PastDueVoiceEnabled} label="Voice" />
            <SwitchRow {...page} keyName={LocationVarKey.FMSplus0PastDueSMSEnabled} label="SMS" />
            <InputRow {...page} hideIf={IF_ANY([IS_NOT_EQUAL(LocationVarKey.FMSplus0PastDueSMSEnabled, "1"), IS_NOT_EQUAL(LocationVarKey.FMSplusPastDueShortlinksEnabled, "1")])} keyName={LocationVarKey.FMSplus0PastDueSMSShortlink} label="SMS Link to Shorten"/>
            <SwitchRow {...page} keyName={LocationVarKey.FMSplus0PastDueEmailEnabled} label="Email" />
            <InputRow {...page} hideIf={IF_ANY([IS_NOT_EQUAL(LocationVarKey.FMSplus0PastDueEmailEnabled, "1"), IS_NOT_EQUAL(LocationVarKey.FMSplusPastDueShortlinksEnabled, "1")])} keyName={LocationVarKey.FMSplus0PastDueEmailShortlink} label="Email Link to Shorten"/>

            <TextAreaRow
              {...page}
              hideIf={IF_ANY([IS_NOT_EQUAL(LocationVarKey.FMSplus0PastDueVoiceEnabled, "1"), IS_NOT_EQUAL(LocationVarKey.swivlHasVoice, "1")])}
              marginTop
              label=" Voice Message"
              keyName={LocationVarKey.FMSplus0PastDueVoiceMessage}
            />

            <Section {...page} hideIf={IS_NOT_EQUAL(LocationVarKey.FMSplus0PastDueSMSEnabled, "1")}>
              <TextAreaRow {...page} marginTop label=" SMS Message 1" keyName={LocationVarKey.FMSplus0PastDue1} />
              <TextAreaRow
                {...page}
                hideIf={IS_EQUAL(LocationVarKey.FMSplusPastDueShortlinksEnabled, "1")}
                marginTop
                label=" SMS Message 2 (Optional)"
                keyName={LocationVarKey.FMSplus0PastDue2}
                enableSwitchVar={LocationVarKey.FMSplus0PastDue2Enabled}
              />
              <TextAreaRow
                {...page}
                hideIf={IS_NOT_EQUAL(LocationVarKey.FMSplusPastDueShortlinksEnabled, "1")}
                marginTop
                label=" SMS Message 2 (Optional)"
                keyName={LocationVarKey.FMSplus0PastDue2Shortlink}
                enableSwitchVar={LocationVarKey.FMSplus0PastDue2Enabled}
              />
              <TextAreaRow
                {...page}
                marginTop
                label=" SMS Message 3 (Optional)"
                keyName={LocationVarKey.FMSplus0PastDue3}
                enableSwitchVar={LocationVarKey.FMSplus0PastDue3Enabled}
              />
            </Section>
          </Section>
        </>
      )}

      {sequenceArray && SequenceHelper.isAnySequenceSubType(SequenceSubType.PAST_DUE_DAY_01, sequenceArray) && (
        <>
          <SectionHeader {...page}>1 day after past due</SectionHeader>
          <DropdownRow
            {...page}
            label="Enabled"
            marginTop
            keyName={LocationVarKey.SequencePastDueDay01Enabled}
            options={[
              { label: "Enabled", value: SequenceEnabledStatus.ENABLED },
              { label: "Disabled", value: SequenceEnabledStatus.DISABLED },
              { label: "Simulated", value: SequenceEnabledStatus.SIMULATED },
            ]}
          />
          <Section {...page} hideIf={IS_EQUAL(LocationVarKey.SequencePastDueDay01Enabled, SequenceEnabledStatus.DISABLED)}>
            <DropdownRow
              {...page}
              label="Time"
              marginTop
              keyName={LocationVarKey.SequencePastDueDay01TimeInUTC}
              options={OptionsHelper.hoursOptionsLocalized()}
            />
            <h6>
              <b>Channel</b>
            </h6>
            <br></br>
            <SwitchRow {...page} hideIf={IS_NOT_EQUAL(LocationVarKey.swivlHasVoice, "1")} keyName={LocationVarKey.FMSplusp1PastDueVoiceEnabled} label="Voice" />
            <SwitchRow {...page} keyName={LocationVarKey.FMSplusp1PastDueSMSEnabled} label="SMS" />
            <InputRow {...page} hideIf={IF_ANY([IS_NOT_EQUAL(LocationVarKey.FMSplusp1PastDueSMSEnabled, "1"), IS_NOT_EQUAL(LocationVarKey.FMSplusPastDueShortlinksEnabled, "1")])} keyName={LocationVarKey.FMSplusp1PastDueSMSShortlink} label="SMS Link to Shorten"/>
            <SwitchRow {...page} keyName={LocationVarKey.FMSplusp1PastDueEmailEnabled} label="Email" />
            <InputRow {...page} hideIf={IF_ANY([IS_NOT_EQUAL(LocationVarKey.FMSplusp1PastDueEmailEnabled, "1"), IS_NOT_EQUAL(LocationVarKey.FMSplusPastDueShortlinksEnabled, "1")])} keyName={LocationVarKey.FMSplusp1PastDueEmailShortlink} label="Email Link to Shorten"/>

            <TextAreaRow
              {...page}
              hideIf={IF_ANY([IS_NOT_EQUAL(LocationVarKey.FMSplusp1PastDueVoiceEnabled, "1"), IS_NOT_EQUAL(LocationVarKey.swivlHasVoice, "1")])}
              marginTop
              label=" Voice Message"
              keyName={LocationVarKey.FMSplusp1PastDueVoiceMessage}
            />

            <Section {...page} hideIf={IS_NOT_EQUAL(LocationVarKey.FMSplusp1PastDueSMSEnabled, "1")}>
              <TextAreaRow {...page} marginTop label=" SMS Message 1" keyName={LocationVarKey.FMSplusp1PastDue1} />
              <TextAreaRow
                {...page}
                hideIf={IS_EQUAL(LocationVarKey.FMSplusPastDueShortlinksEnabled, "1")}
                marginTop
                label=" SMS Message 2 (Optional)"
                keyName={LocationVarKey.FMSplusp1PastDue2}
                enableSwitchVar={LocationVarKey.FMSplusp1PastDue2Enabled}
              />
              <TextAreaRow
                {...page}
                hideIf={IS_NOT_EQUAL(LocationVarKey.FMSplusPastDueShortlinksEnabled, "1")}
                marginTop
                label=" SMS Message 2 (Optional)"
                keyName={LocationVarKey.FMSplusp1PastDue2Shortlink}
                enableSwitchVar={LocationVarKey.FMSplusp1PastDue2Enabled}
              />
              <TextAreaRow
                {...page}
                marginTop
                label=" SMS Message 3 (Optional)"
                keyName={LocationVarKey.FMSplusp1PastDue3}
                enableSwitchVar={LocationVarKey.FMSplusp1PastDue3Enabled}
              />
            </Section>
          </Section>
        </>
      )}

      {sequenceArray && SequenceHelper.isAnySequenceSubType(SequenceSubType.PAST_DUE_DAY_02, sequenceArray) && (
        <>
          <SectionHeader {...page}>2 days after past due</SectionHeader>
          <DropdownRow
            {...page}
            label="Enabled"
            marginTop
            keyName={LocationVarKey.SequencePastDueDay02Enabled}
            options={[
              { label: "Enabled", value: SequenceEnabledStatus.ENABLED },
              { label: "Disabled", value: SequenceEnabledStatus.DISABLED },
              { label: "Simulated", value: SequenceEnabledStatus.SIMULATED },
            ]}
          />
          <Section {...page} hideIf={IS_EQUAL(LocationVarKey.SequencePastDueDay02Enabled, SequenceEnabledStatus.DISABLED)}>
            <DropdownRow
              {...page}
              label="Time"
              marginTop
              keyName={LocationVarKey.SequencePastDueDay02TimeInUTC}
              options={OptionsHelper.hoursOptionsLocalized()}
            />
            <h6>
              <b>Channel</b>
            </h6>
            <br></br>
            <SwitchRow {...page} hideIf={IS_NOT_EQUAL(LocationVarKey.swivlHasVoice, "1")} keyName={LocationVarKey.FMSplusp2PastDueVoiceEnabled} label="Voice" />
            <SwitchRow {...page} keyName={LocationVarKey.FMSplusp2PastDueSMSEnabled} label="SMS" />
            <InputRow {...page} hideIf={IF_ANY([IS_NOT_EQUAL(LocationVarKey.FMSplusp2PastDueSMSEnabled, "1"), IS_NOT_EQUAL(LocationVarKey.FMSplusPastDueShortlinksEnabled, "1")])} keyName={LocationVarKey.FMSplusp2PastDueSMSShortlink} label="SMS Link to Shorten"/>
            <SwitchRow {...page} keyName={LocationVarKey.FMSplusp2PastDueEmailEnabled} label="Email" />
            <InputRow {...page} hideIf={IF_ANY([IS_NOT_EQUAL(LocationVarKey.FMSplusp2PastDueEmailEnabled, "1"), IS_NOT_EQUAL(LocationVarKey.FMSplusPastDueShortlinksEnabled, "1")])} keyName={LocationVarKey.FMSplusp2PastDueEmailShortlink} label="Email Link to Shorten"/>

            <TextAreaRow
              {...page}
              hideIf={IF_ANY([IS_NOT_EQUAL(LocationVarKey.FMSplusp2PastDueVoiceEnabled, "1"), IS_NOT_EQUAL(LocationVarKey.swivlHasVoice, "1")])}
              marginTop
              label=" Voice Message"
              keyName={LocationVarKey.FMSplusp2PastDueVoiceMessage}
            />

            <Section {...page} hideIf={IS_NOT_EQUAL(LocationVarKey.FMSplusp2PastDueSMSEnabled, "1")}>
              <TextAreaRow {...page} marginTop label=" SMS Message 1" keyName={LocationVarKey.FMSplusp2PastDue1} />
              <TextAreaRow
                {...page}
                hideIf={IS_EQUAL(LocationVarKey.FMSplusPastDueShortlinksEnabled, "1")}
                marginTop
                label=" SMS Message 2 (Optional)"
                keyName={LocationVarKey.FMSplusp2PastDue2}
                enableSwitchVar={LocationVarKey.FMSplusp2PastDue2Enabled}
              />
              <TextAreaRow
                {...page}
                hideIf={IS_NOT_EQUAL(LocationVarKey.FMSplusPastDueShortlinksEnabled, "1")}
                marginTop
                label=" SMS Message 2 (Optional)"
                keyName={LocationVarKey.FMSplusp2PastDue2Shortlink}
                enableSwitchVar={LocationVarKey.FMSplusp2PastDue2Enabled}
              />
              <TextAreaRow
                {...page}
                marginTop
                label=" SMS Message 3 (Optional)"
                keyName={LocationVarKey.FMSplusp2PastDue3}
                enableSwitchVar={LocationVarKey.FMSplusp2PastDue3Enabled}
              />
            </Section>
          </Section>
        </>
      )}

      {sequenceArray && SequenceHelper.isAnySequenceSubType(SequenceSubType.PAST_DUE_DAY_03, sequenceArray) && (
        <>
          <SectionHeader {...page}>3 days after past due</SectionHeader>
          <DropdownRow
            {...page}
            label="Enabled"
            marginTop
            keyName={LocationVarKey.SequencePastDueDay03Enabled}
            options={[
              { label: "Enabled", value: SequenceEnabledStatus.ENABLED },
              { label: "Disabled", value: SequenceEnabledStatus.DISABLED },
              { label: "Simulated", value: SequenceEnabledStatus.SIMULATED },
            ]}
          />
          <Section {...page} hideIf={IS_EQUAL(LocationVarKey.SequencePastDueDay03Enabled, SequenceEnabledStatus.DISABLED)}>
            <DropdownRow
              {...page}
              label="Time"
              marginTop
              keyName={LocationVarKey.SequencePastDueDay03TimeInUTC}
              options={OptionsHelper.hoursOptionsLocalized()}
            />
            <h6>
              <b>Channel</b>
            </h6>
            <br></br>
            <SwitchRow {...page} hideIf={IS_NOT_EQUAL(LocationVarKey.swivlHasVoice, "1")} keyName={LocationVarKey.FMSplusp3PastDueVoiceEnabled} label="Voice" />
            <SwitchRow {...page} keyName={LocationVarKey.FMSplusp3PastDueSMSEnabled} label="SMS" />
            <InputRow {...page} hideIf={IF_ANY([IS_NOT_EQUAL(LocationVarKey.FMSplusp3PastDueSMSEnabled, "1"), IS_NOT_EQUAL(LocationVarKey.FMSplusPastDueShortlinksEnabled, "1")])} keyName={LocationVarKey.FMSplusp3PastDueSMSShortlink} label="SMS Link to Shorten"/>
            <SwitchRow {...page} keyName={LocationVarKey.FMSplusp3PastDueEmailEnabled} label="Email" />
            <InputRow {...page} hideIf={IF_ANY([IS_NOT_EQUAL(LocationVarKey.FMSplusp3PastDueEmailEnabled, "1"), IS_NOT_EQUAL(LocationVarKey.FMSplusPastDueShortlinksEnabled, "1")])} keyName={LocationVarKey.FMSplusp3PastDueEmailShortlink} label="Email Link to Shorten"/>

            <TextAreaRow
              {...page}
              hideIf={IF_ANY([IS_NOT_EQUAL(LocationVarKey.FMSplusp3PastDueVoiceEnabled, "1"), IS_NOT_EQUAL(LocationVarKey.swivlHasVoice, "1")])}
              marginTop
              label=" Voice Message"
              keyName={LocationVarKey.FMSplusp3PastDueVoiceMessage}
            />

            <Section {...page} hideIf={IS_NOT_EQUAL(LocationVarKey.FMSplusp3PastDueSMSEnabled, "1")}>
              <TextAreaRow {...page} marginTop label=" SMS Message 1" keyName={LocationVarKey.FMSplusp3PastDue1} />
              <TextAreaRow
                {...page}
                hideIf={IS_EQUAL(LocationVarKey.FMSplusPastDueShortlinksEnabled, "1")}
                marginTop
                label=" SMS Message 2 (Optional)"
                keyName={LocationVarKey.FMSplusp3PastDue2}
                enableSwitchVar={LocationVarKey.FMSplusp3PastDue2Enabled}
              />
              <TextAreaRow
                {...page}
                hideIf={IS_NOT_EQUAL(LocationVarKey.FMSplusPastDueShortlinksEnabled, "1")}
                marginTop
                label=" SMS Message 2 (Optional)"
                keyName={LocationVarKey.FMSplusp3PastDue2Shortlink}
                enableSwitchVar={LocationVarKey.FMSplusp3PastDue2Enabled}
              />
              <TextAreaRow
                {...page}
                marginTop
                label=" SMS Message 3 (Optional)"
                keyName={LocationVarKey.FMSplusp3PastDue3}
                enableSwitchVar={LocationVarKey.FMSplusp3PastDue3Enabled}
              />
            </Section>
          </Section>
        </>
      )}

      {sequenceArray && SequenceHelper.isAnySequenceSubType(SequenceSubType.PAST_DUE_DAY_05, sequenceArray) && (
        <>
          <SectionHeader {...page}>5 days after past due</SectionHeader>
          <DropdownRow
            {...page}
            label="Enabled"
            marginTop
            keyName={LocationVarKey.SequencePastDueDay05Enabled}
            options={[
              { label: "Enabled", value: SequenceEnabledStatus.ENABLED },
              { label: "Disabled", value: SequenceEnabledStatus.DISABLED },
              { label: "Simulated", value: SequenceEnabledStatus.SIMULATED },
            ]}
          />
          <Section {...page} hideIf={IS_EQUAL(LocationVarKey.SequencePastDueDay05Enabled, SequenceEnabledStatus.DISABLED)}>
            <DropdownRow
              {...page}
              label="Time"
              marginTop
              keyName={LocationVarKey.SequencePastDueDay05TimeInUTC}
              options={OptionsHelper.hoursOptionsLocalized()}
            />
            <h6>
              <b>Channel</b>
            </h6>
            <br></br>
            <SwitchRow {...page} hideIf={IS_NOT_EQUAL(LocationVarKey.swivlHasVoice, "1")} keyName={LocationVarKey.FMSplusp5PastDueVoiceEnabled} label="Voice" />
            <SwitchRow {...page} keyName={LocationVarKey.FMSplusp5PastDueSMSEnabled} label="SMS" />
            <InputRow {...page} hideIf={IF_ANY([IS_NOT_EQUAL(LocationVarKey.FMSplusp5PastDueSMSEnabled, "1"), IS_NOT_EQUAL(LocationVarKey.FMSplusPastDueShortlinksEnabled, "1")])} keyName={LocationVarKey.FMSplusp5PastDueSMSShortlink} label="SMS Link to Shorten"/>
            <SwitchRow {...page} keyName={LocationVarKey.FMSplusp5PastDueEmailEnabled} label="Email" />
            <InputRow {...page} hideIf={IF_ANY([IS_NOT_EQUAL(LocationVarKey.FMSplusp5PastDueEmailEnabled, "1"), IS_NOT_EQUAL(LocationVarKey.FMSplusPastDueShortlinksEnabled, "1")])} keyName={LocationVarKey.FMSplusp5PastDueEmailShortlink} label="Email Link to Shorten"/>

            <TextAreaRow
              {...page}
              hideIf={IF_ANY([IS_NOT_EQUAL(LocationVarKey.FMSplusp5PastDueVoiceEnabled, "1"), IS_NOT_EQUAL(LocationVarKey.swivlHasVoice, "1")])}
              marginTop
              label=" Voice Message"
              keyName={LocationVarKey.FMSplusp5PastDueVoiceMessage}
            />

            <Section {...page} hideIf={IS_NOT_EQUAL(LocationVarKey.FMSplusp5PastDueSMSEnabled, "1")}>
              <TextAreaRow {...page} marginTop label=" SMS Message 1" keyName={LocationVarKey.FMSplusp5PastDue1} />
              <TextAreaRow
                {...page}
                hideIf={IS_EQUAL(LocationVarKey.FMSplusPastDueShortlinksEnabled, "1")}
                marginTop
                label=" SMS Message 2 (Optional)"
                keyName={LocationVarKey.FMSplusp5PastDue2}
                enableSwitchVar={LocationVarKey.FMSplusp5PastDue2Enabled}
              />
              <TextAreaRow
                {...page}
                hideIf={IS_NOT_EQUAL(LocationVarKey.FMSplusPastDueShortlinksEnabled, "1")}
                marginTop
                label=" SMS Message 2 (Optional)"
                keyName={LocationVarKey.FMSplusp5PastDue2Shortlink}
                enableSwitchVar={LocationVarKey.FMSplusp5PastDue2Enabled}
              />
              <TextAreaRow
                {...page}
                marginTop
                label=" SMS Message 3 (Optional)"
                keyName={LocationVarKey.FMSplusp5PastDue3}
                enableSwitchVar={LocationVarKey.FMSplusp5PastDue3Enabled}
              />
            </Section>
          </Section>
        </>
      )}

      {sequenceArray && SequenceHelper.isAnySequenceSubType(SequenceSubType.PAST_DUE_DAY_07, sequenceArray) && (
        <>
          <SectionHeader {...page}>7 days after past due</SectionHeader>
          <DropdownRow
            {...page}
            label="Enabled"
            marginTop
            keyName={LocationVarKey.SequencePastDueDay07Enabled}
            options={[
              { label: "Enabled", value: SequenceEnabledStatus.ENABLED },
              { label: "Disabled", value: SequenceEnabledStatus.DISABLED },
              { label: "Simulated", value: SequenceEnabledStatus.SIMULATED },
            ]}
          />
          <Section {...page} hideIf={IS_EQUAL(LocationVarKey.SequencePastDueDay07Enabled, SequenceEnabledStatus.DISABLED)}>
            <DropdownRow
              {...page}
              label="Time"
              marginTop
              keyName={LocationVarKey.SequencePastDueDay07TimeInUTC}
              options={OptionsHelper.hoursOptionsLocalized()}
            />
            <h6>
              <b>Channel</b>
            </h6>
            <br></br>
            <SwitchRow {...page} hideIf={IS_NOT_EQUAL(LocationVarKey.swivlHasVoice, "1")} keyName={LocationVarKey.FMSplusp7PastDueVoiceEnabled} label="Voice" />
            <SwitchRow {...page} keyName={LocationVarKey.FMSplusp7PastDueSMSEnabled} label="SMS" />
            <InputRow {...page} hideIf={IF_ANY([IS_NOT_EQUAL(LocationVarKey.FMSplusp7PastDueSMSEnabled, "1"), IS_NOT_EQUAL(LocationVarKey.FMSplusPastDueShortlinksEnabled, "1")])} keyName={LocationVarKey.FMSplusp7PastDueSMSShortlink} label="SMS Link to Shorten"/>
            <SwitchRow {...page} keyName={LocationVarKey.FMSplusp7PastDueEmailEnabled} label="Email" />
            <InputRow {...page} hideIf={IF_ANY([IS_NOT_EQUAL(LocationVarKey.FMSplusp7PastDueEmailEnabled, "1"), IS_NOT_EQUAL(LocationVarKey.FMSplusPastDueShortlinksEnabled, "1")])} keyName={LocationVarKey.FMSplusp7PastDueEmailShortlink} label="Email Link to Shorten"/>


            <TextAreaRow
              {...page}
              hideIf={IF_ANY([IS_NOT_EQUAL(LocationVarKey.FMSplusp7PastDueVoiceEnabled, "1"), IS_NOT_EQUAL(LocationVarKey.swivlHasVoice, "1")])}
              marginTop
              label=" Voice Message"
              keyName={LocationVarKey.FMSplusp7PastDueVoiceMessage}
            />

            <Section {...page} hideIf={IS_NOT_EQUAL(LocationVarKey.FMSplusp7PastDueSMSEnabled, "1")}>
              <TextAreaRow {...page} marginTop label=" SMS Message 1" keyName={LocationVarKey.FMSplusp7PastDue1} />
              <TextAreaRow
                {...page}
                hideIf={IS_EQUAL(LocationVarKey.FMSplusPastDueShortlinksEnabled, "1")}
                marginTop
                label=" SMS Message 2 (Optional)"
                keyName={LocationVarKey.FMSplusp7PastDue2}
                enableSwitchVar={LocationVarKey.FMSplusp7PastDue2Enabled}
              />
              <TextAreaRow
                {...page}
                hideIf={IS_NOT_EQUAL(LocationVarKey.FMSplusPastDueShortlinksEnabled, "1")}
                marginTop
                label=" SMS Message 2 (Optional)"
                keyName={LocationVarKey.FMSplusp7PastDue2Shortlink}
                enableSwitchVar={LocationVarKey.FMSplusp7PastDue2Enabled}
              />
              <TextAreaRow
                {...page}
                marginTop
                label=" SMS Message 3 (Optional)"
                keyName={LocationVarKey.FMSplusp7PastDue3}
                enableSwitchVar={LocationVarKey.FMSplusp7PastDue3Enabled}
              />
            </Section>
          </Section>
        </>
      )}

      {sequenceArray && SequenceHelper.isAnySequenceSubType(SequenceSubType.PAST_DUE_DAY_10, sequenceArray) && (
        <>
          <SectionHeader {...page}>10 days after past due</SectionHeader>
          <DropdownRow
            {...page}
            label="Enabled"
            marginTop
            keyName={LocationVarKey.SequencePastDueDay10Enabled}
            options={[
              { label: "Enabled", value: SequenceEnabledStatus.ENABLED },
              { label: "Disabled", value: SequenceEnabledStatus.DISABLED },
              { label: "Simulated", value: SequenceEnabledStatus.SIMULATED },
            ]}
          />
          <Section {...page} hideIf={IS_EQUAL(LocationVarKey.SequencePastDueDay10Enabled, SequenceEnabledStatus.DISABLED)}>
            <DropdownRow
              {...page}
              label="Time"
              marginTop
              keyName={LocationVarKey.SequencePastDueDay10TimeInUTC}
              options={OptionsHelper.hoursOptionsLocalized()}
            />
            <h6>
              <b>Channel</b>
            </h6>
            <br></br>
            <SwitchRow {...page} hideIf={IS_NOT_EQUAL(LocationVarKey.swivlHasVoice, "1")} keyName={LocationVarKey.FMSplusp10PastDueVoiceEnabled} label="Voice" />
            <SwitchRow {...page} keyName={LocationVarKey.FMSplusp10PastDueSMSEnabled} label="SMS" />
            <InputRow {...page} hideIf={IF_ANY([IS_NOT_EQUAL(LocationVarKey.FMSplusp10PastDueSMSEnabled, "1"), IS_NOT_EQUAL(LocationVarKey.FMSplusPastDueShortlinksEnabled, "1")])} keyName={LocationVarKey.FMSplusp10PastDueSMSShortlink} label="SMS Link to Shorten"/>
            <SwitchRow {...page} keyName={LocationVarKey.FMSplusp10PastDueEmailEnabled} label="Email" />
            <InputRow {...page} hideIf={IF_ANY([IS_NOT_EQUAL(LocationVarKey.FMSplusp10PastDueEmailEnabled, "1"), IS_NOT_EQUAL(LocationVarKey.FMSplusPastDueShortlinksEnabled, "1")])} keyName={LocationVarKey.FMSplusp10PastDueEmailShortlink} label="Email Link to Shorten"/>


            <TextAreaRow
              {...page}
              hideIf={IF_ANY([IS_NOT_EQUAL(LocationVarKey.FMSplusp10PastDueVoiceEnabled, "1"), IS_NOT_EQUAL(LocationVarKey.swivlHasVoice, "1")])}
              marginTop
              label=" Voice Message"
              keyName={LocationVarKey.FMSplusp10PastDueVoiceMessage}
            />

            <Section {...page} hideIf={IS_NOT_EQUAL(LocationVarKey.FMSplusp10PastDueSMSEnabled, "1")}>
              <TextAreaRow {...page} marginTop label=" SMS Message 1" keyName={LocationVarKey.FMSplusp10PastDue1} />
              <TextAreaRow
                {...page}
                hideIf={IS_EQUAL(LocationVarKey.FMSplusPastDueShortlinksEnabled, "1")}
                marginTop
                label=" SMS Message 2 (Optional)"
                keyName={LocationVarKey.FMSplusp10PastDue2}
                enableSwitchVar={LocationVarKey.FMSplusp10PastDue2Enabled}
              />
              <TextAreaRow
                {...page}
                hideIf={IS_NOT_EQUAL(LocationVarKey.FMSplusPastDueShortlinksEnabled, "1")}
                marginTop
                label=" SMS Message 2 (Optional)"
                keyName={LocationVarKey.FMSplusp10PastDue2Shortlink}
                enableSwitchVar={LocationVarKey.FMSplusp10PastDue2Enabled}
              />
              <TextAreaRow
                {...page}
                marginTop
                label=" SMS Message 3 (Optional)"
                keyName={LocationVarKey.FMSplusp10PastDue3}
                enableSwitchVar={LocationVarKey.FMSplusp10PastDue3Enabled}
              />
            </Section>
          </Section>
        </>
      )}

      {sequenceArray && SequenceHelper.isAnySequenceSubType(SequenceSubType.PAST_DUE_DAY_14, sequenceArray) && (
        <>
          <SectionHeader {...page}>14 days after past due</SectionHeader>
          <DropdownRow
            {...page}
            label="Enabled"
            marginTop
            keyName={LocationVarKey.SequencePastDueDay14Enabled}
            options={[
              { label: "Enabled", value: SequenceEnabledStatus.ENABLED },
              { label: "Disabled", value: SequenceEnabledStatus.DISABLED },
              { label: "Simulated", value: SequenceEnabledStatus.SIMULATED },
            ]}
          />
          <Section {...page} hideIf={IS_EQUAL(LocationVarKey.SequencePastDueDay14Enabled, SequenceEnabledStatus.DISABLED)}>
            <DropdownRow
              {...page}
              label="Time"
              marginTop
              keyName={LocationVarKey.SequencePastDueDay14TimeInUTC}
              options={OptionsHelper.hoursOptionsLocalized()}
            />
            <h6>
              <b>Channel</b>
            </h6>
            <br></br>
            <SwitchRow {...page} hideIf={IS_NOT_EQUAL(LocationVarKey.swivlHasVoice, "1")} keyName={LocationVarKey.FMSplusp14PastDueVoiceEnabled} label="Voice" />
            <SwitchRow {...page} keyName={LocationVarKey.FMSplusp14PastDueSMSEnabled} label="SMS" />
            <InputRow {...page} hideIf={IF_ANY([IS_NOT_EQUAL(LocationVarKey.FMSplusp14PastDueSMSEnabled, "1"), IS_NOT_EQUAL(LocationVarKey.FMSplusPastDueShortlinksEnabled, "1")])} keyName={LocationVarKey.FMSplusp14PastDueSMSShortlink} label="SMS Link to Shorten"/>
            <SwitchRow {...page} keyName={LocationVarKey.FMSplusp14PastDueEmailEnabled} label="Email" />
            <InputRow {...page} hideIf={IF_ANY([IS_NOT_EQUAL(LocationVarKey.FMSplusp14PastDueEmailEnabled, "1"), IS_NOT_EQUAL(LocationVarKey.FMSplusPastDueShortlinksEnabled, "1")])} keyName={LocationVarKey.FMSplusp14PastDueEmailShortlink} label="Email Link to Shorten"/>


            <TextAreaRow
              {...page}
              hideIf={IF_ANY([IS_NOT_EQUAL(LocationVarKey.FMSplusp14PastDueVoiceEnabled, "1"), IS_NOT_EQUAL(LocationVarKey.swivlHasVoice, "1")])}
              marginTop
              label=" Voice Message"
              keyName={LocationVarKey.FMSplusp14PastDueVoiceMessage}
            />

            <Section {...page} hideIf={IS_NOT_EQUAL(LocationVarKey.FMSplusp14PastDueSMSEnabled, "1")}>
              <TextAreaRow {...page} marginTop label=" SMS Message 1" keyName={LocationVarKey.FMSplusp14PastDue1} />
              <TextAreaRow
                {...page}
                hideIf={IS_EQUAL(LocationVarKey.FMSplusPastDueShortlinksEnabled, "1")}
                marginTop
                label=" SMS Message 2 (Optional)"
                keyName={LocationVarKey.FMSplusp14PastDue2}
                enableSwitchVar={LocationVarKey.FMSplusp14PastDue2Enabled}
              />
              <TextAreaRow
                {...page}
                hideIf={IS_NOT_EQUAL(LocationVarKey.FMSplusPastDueShortlinksEnabled, "1")}
                marginTop
                label=" SMS Message 2 (Optional)"
                keyName={LocationVarKey.FMSplusp14PastDue2Shortlink}
                enableSwitchVar={LocationVarKey.FMSplusp14PastDue2Enabled}
              />
              <TextAreaRow
                {...page}
                marginTop
                label=" SMS Message 3 (Optional)"
                keyName={LocationVarKey.FMSplusp14PastDue3}
                enableSwitchVar={LocationVarKey.FMSplusp14PastDue3Enabled}
              />
            </Section>
          </Section>
        </>
      )}

      {sequenceArray && SequenceHelper.isAnySequenceSubType(SequenceSubType.PAST_DUE_DAY_30, sequenceArray) && (
        <>
          <SectionHeader {...page}>30 days after past due</SectionHeader>
          <DropdownRow
            {...page}
            label="Enabled"
            marginTop
            keyName={LocationVarKey.SequencePastDueDay30Enabled}
            options={[
              { label: "Enabled", value: SequenceEnabledStatus.ENABLED },
              { label: "Disabled", value: SequenceEnabledStatus.DISABLED },
              { label: "Simulated", value: SequenceEnabledStatus.SIMULATED },
            ]}
          />
          <Section {...page} hideIf={IS_EQUAL(LocationVarKey.SequencePastDueDay30Enabled, SequenceEnabledStatus.DISABLED)}>
            <DropdownRow
              {...page}
              label="Time"
              marginTop
              keyName={LocationVarKey.SequencePastDueDay30TimeInUTC}
              options={OptionsHelper.hoursOptionsLocalized()}
            />
            <h6>
              <b>Channel</b>
            </h6>
            <br></br>
            <SwitchRow {...page} hideIf={IS_NOT_EQUAL(LocationVarKey.swivlHasVoice, "1")} keyName={LocationVarKey.FMSplusp30PastDueVoiceEnabled} label="Voice" />
            <SwitchRow {...page} keyName={LocationVarKey.FMSplusp30PastDueSMSEnabled} label="SMS" />
            <InputRow {...page} hideIf={IF_ANY([IS_NOT_EQUAL(LocationVarKey.FMSplusp30PastDueSMSEnabled, "1"), IS_NOT_EQUAL(LocationVarKey.FMSplusPastDueShortlinksEnabled, "1")])} keyName={LocationVarKey.FMSplusp30PastDueSMSShortlink} label="SMS Link to Shorten"/>
            <SwitchRow {...page} keyName={LocationVarKey.FMSplusp30PastDueEmailEnabled} label="Email" />
            <InputRow {...page} hideIf={IF_ANY([IS_NOT_EQUAL(LocationVarKey.FMSplusp30PastDueEmailEnabled, "1"), IS_NOT_EQUAL(LocationVarKey.FMSplusPastDueShortlinksEnabled, "1")])} keyName={LocationVarKey.FMSplusp30PastDueEmailShortlink} label="Email Link to Shorten"/>


            <TextAreaRow
              {...page}
              hideIf={IF_ANY([IS_NOT_EQUAL(LocationVarKey.FMSplusp30PastDueVoiceEnabled, "1"), IS_NOT_EQUAL(LocationVarKey.swivlHasVoice, "1")])}
              marginTop
              label=" Voice Message"
              keyName={LocationVarKey.FMSplusp30PastDueVoiceMessage}
            />

            <Section {...page} hideIf={IS_NOT_EQUAL(LocationVarKey.FMSplusp30PastDueSMSEnabled, "1")}>
              <TextAreaRow {...page} marginTop label=" SMS Message 1" keyName={LocationVarKey.FMSplusp30PastDue1} />
              <TextAreaRow
                {...page}
                hideIf={IS_EQUAL(LocationVarKey.FMSplusPastDueShortlinksEnabled, "1")}
                marginTop
                label=" SMS Message 2 (Optional)"
                keyName={LocationVarKey.FMSplusp30PastDue2}
                enableSwitchVar={LocationVarKey.FMSplusp30PastDue2Enabled}
              />
              <TextAreaRow
                {...page}
                hideIf={IS_NOT_EQUAL(LocationVarKey.FMSplusPastDueShortlinksEnabled, "1")}
                marginTop
                label=" SMS Message 2 (Optional)"
                keyName={LocationVarKey.FMSplusp30PastDue2Shortlink}
                enableSwitchVar={LocationVarKey.FMSplusp30PastDue2Enabled}
              />
              <TextAreaRow
                {...page}
                marginTop
                label=" SMS Message 3 (Optional)"
                keyName={LocationVarKey.FMSplusp30PastDue3}
                enableSwitchVar={LocationVarKey.FMSplusp30PastDue3Enabled}
              />
            </Section>
          </Section>
        </>
      )}

      {sequenceArray && SequenceHelper.isAnySequenceSubType(SequenceSubType.PAST_DUE_DAY_45, sequenceArray) && (
        <>
          <SectionHeader {...page}>45 days after past due</SectionHeader>
          <DropdownRow
            {...page}
            label="Enabled"
            marginTop
            keyName={LocationVarKey.SequencePastDueDay45Enabled}
            options={[
              { label: "Enabled", value: SequenceEnabledStatus.ENABLED },
              { label: "Disabled", value: SequenceEnabledStatus.DISABLED },
              { label: "Simulated", value: SequenceEnabledStatus.SIMULATED },
            ]}
          />
          <Section {...page} hideIf={IS_EQUAL(LocationVarKey.SequencePastDueDay45Enabled, SequenceEnabledStatus.DISABLED)}>
            <DropdownRow
              {...page}
              label="Time"
              marginTop
              keyName={LocationVarKey.SequencePastDueDay45TimeInUTC}
              options={OptionsHelper.hoursOptionsLocalized()}
            />
            <h6>
              <b>Channel</b>
            </h6>
            <br></br>
            <SwitchRow {...page} hideIf={IS_NOT_EQUAL(LocationVarKey.swivlHasVoice, "1")} keyName={LocationVarKey.FMSplusp45PastDueVoiceEnabled} label="Voice" />
            <SwitchRow {...page} keyName={LocationVarKey.FMSplusp45PastDueSMSEnabled} label="SMS" />
            <InputRow {...page} hideIf={IF_ANY([IS_NOT_EQUAL(LocationVarKey.FMSplusp45PastDueSMSEnabled, "1"), IS_NOT_EQUAL(LocationVarKey.FMSplusPastDueShortlinksEnabled, "1")])} keyName={LocationVarKey.FMSplusp45PastDueSMSShortlink} label="SMS Link to Shorten"/>
            <SwitchRow {...page} keyName={LocationVarKey.FMSplusp45PastDueEmailEnabled} label="Email" />
            <InputRow {...page} hideIf={IF_ANY([IS_NOT_EQUAL(LocationVarKey.FMSplusp45PastDueEmailEnabled, "1"), IS_NOT_EQUAL(LocationVarKey.FMSplusPastDueShortlinksEnabled, "1")])} keyName={LocationVarKey.FMSplusp45PastDueEmailShortlink} label="Email Link to Shorten"/>


            <TextAreaRow
              {...page}
              hideIf={IF_ANY([IS_NOT_EQUAL(LocationVarKey.FMSplusp45PastDueVoiceEnabled, "1"), IS_NOT_EQUAL(LocationVarKey.swivlHasVoice, "1")])}
              marginTop
              label=" Voice Message"
              keyName={LocationVarKey.FMSplusp45PastDueVoiceMessage}
            />

            <Section {...page} hideIf={IS_NOT_EQUAL(LocationVarKey.FMSplusp45PastDueSMSEnabled, "1")}>
              <TextAreaRow {...page} marginTop label=" SMS Message 1" keyName={LocationVarKey.FMSplusp45PastDue1} />
              <TextAreaRow
                {...page}
                hideIf={IS_EQUAL(LocationVarKey.FMSplusPastDueShortlinksEnabled, "1")}
                marginTop
                label=" SMS Message 2 (Optional)"
                keyName={LocationVarKey.FMSplusp45PastDue2}
                enableSwitchVar={LocationVarKey.FMSplusp45PastDue2Enabled}
              />
              <TextAreaRow
                {...page}
                hideIf={IS_NOT_EQUAL(LocationVarKey.FMSplusPastDueShortlinksEnabled, "1")}
                marginTop
                label=" SMS Message 2 (Optional)"
                keyName={LocationVarKey.FMSplusp45PastDue2Shortlink}
                enableSwitchVar={LocationVarKey.FMSplusp45PastDue2Enabled}
              />
              <TextAreaRow
                {...page}
                marginTop
                label=" SMS Message 3 (Optional)"
                keyName={LocationVarKey.FMSplusp45PastDue3}
                enableSwitchVar={LocationVarKey.FMSplusp45PastDue3Enabled}
              />
            </Section>
          </Section>
        </>
      )}
      {sequenceArray && SequenceHelper.isAnySequenceSubType(SequenceSubType.CUSTOM, sequenceArray) && (
        <>
          <SectionHeader {...page}>Recurring reminders</SectionHeader>
          <DropdownRow
            {...page}
            label="Enabled"
            marginTop
            keyName={LocationVarKey.SequencePastDueRecurringEnabled}
            options={[
              { label: "Enabled", value: SequenceEnabledStatus.ENABLED },
              { label: "Disabled", value: SequenceEnabledStatus.DISABLED },
              { label: "Simulated", value: SequenceEnabledStatus.SIMULATED },
            ]}
          />
          <Section {...page} hideIf={IS_EQUAL(LocationVarKey.SequencePastDueRecurringEnabled, SequenceEnabledStatus.DISABLED)}>
            <DropdownRow
              {...page}
              label="Time"
              marginTop
              keyName={LocationVarKey.SequencePastDueRecurringTimeInUTC}
              options={OptionsHelper.hoursOptionsLocalized()}
            />
            <h6>
              <b>Channel</b>
            </h6>
            <br></br>
            <SwitchRow {...page} hideIf={IS_NOT_EQUAL(LocationVarKey.swivlHasVoice, "1")} keyName={LocationVarKey.FMSplusRecurringPastDueVoiceEnabled} label="Voice" />
            <SwitchRow {...page} keyName={LocationVarKey.FMSplusRecurringPastDueSMSEnabled} label="SMS" />
            <InputRow {...page} hideIf={IF_ANY([IS_NOT_EQUAL(LocationVarKey.FMSplusRecurringPastDueSMSEnabled, "1"), IS_NOT_EQUAL(LocationVarKey.FMSplusPastDueShortlinksEnabled, "1")])} keyName={LocationVarKey.FMSplusRecurringPastDueSMSShortlink} label="SMS Link to Shorten"/>
            <SwitchRow {...page} keyName={LocationVarKey.FMSplusRecurringPastDueEmailEnabled} label="Email" />
            <InputRow {...page} hideIf={IF_ANY([IS_NOT_EQUAL(LocationVarKey.FMSplusRecurringPastDueEmailEnabled, "1"), IS_NOT_EQUAL(LocationVarKey.FMSplusPastDueShortlinksEnabled, "1")])} keyName={LocationVarKey.FMSplusRecurringPastDueEmailShortlink} label="Email Link to Shorten"/>

            <TextAreaRow
              {...page}
              hideIf={IF_ANY([IS_NOT_EQUAL(LocationVarKey.FMSplusRecurringPastDueVoiceEnabled, "1"), IS_NOT_EQUAL(LocationVarKey.swivlHasVoice, "1")])}
              marginTop
              label=" Voice Message"
              keyName={LocationVarKey.FMSplusRecurringPastDueVoiceMessage}
            />

            <Section {...page} hideIf={IS_NOT_EQUAL(LocationVarKey.FMSplusRecurringPastDueSMSEnabled, "1")}>
              <TextAreaRow {...page} marginTop label=" SMS Message 1" keyName={LocationVarKey.FMSplusRecurringPastDue1} />
              <TextAreaRow
                {...page}
                hideIf={IS_EQUAL(LocationVarKey.FMSplusPastDueShortlinksEnabled, "1")}
                marginTop
                label=" SMS Message 2 (Optional)"
                keyName={LocationVarKey.FMSplusRecurringPastDue2}
                enableSwitchVar={LocationVarKey.FMSplusRecurringPastDue2Enabled}
              />
              <TextAreaRow
                {...page}
                hideIf={IS_NOT_EQUAL(LocationVarKey.FMSplusPastDueShortlinksEnabled, "1")}
                marginTop
                label=" SMS Message 2 (Optional)"
                keyName={LocationVarKey.FMSplusRecurringPastDue2Shortlink}
                enableSwitchVar={LocationVarKey.FMSplusRecurringPastDue2Enabled}
              />
            </Section>
          </Section>
        </>
      )}
    </>
  );
}
